import React, { useState } from 'react';
import { Stack, Typography, IconButton } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { useGetCardQuery } from '../../api/cardSlice';
import LoadingIndicator from '../LoadingIndicator';
import CategoryView from '../PhysicianDashboard/ReviewPageComponents/CategoryView';
import ReviewTabs from '../PhysicianDashboard/ReviewPageComponents/ReviewTabs';
import ItemDetails from './ItemDetails';
import { useLogUserActionMutation } from '../../api/apiSlice';


function CardComparison({ selectedCards, onClose }) {
    const [selectedTab, setSelectedTab] = useState('physician_a');
    const [selectedItem, setSelectedItem] = useState(null);
    const [logUserAction] = useLogUserActionMutation();
    
    // Get card details for both selected physicians
    const firstCardId = selectedCards.length > 0 ? selectedCards[0].id : null;
    const secondCardId = selectedCards.length > 1 ? selectedCards[1].id : null;
    
    const { data: firstCard, isFetching: isFetchingFirst } = useGetCardQuery(
        firstCardId,
        { skip: !firstCardId }
    );
    
    const { data: secondCard, isFetching: isFetchingSecond } = useGetCardQuery(
        secondCardId,
        { skip: !secondCardId }
    );
    
    const isFetching = isFetchingFirst || isFetchingSecond;

    // Transform card data to match CategoryView's expected format
    const transformCardToDraft = (card) => {
        if (!card) return null;
        
        return {
            items: card.pick_list || {}, 
            recommendations: {}, //manually removing the recs here since we don't want to show recs in comparison
            card: card
        };
    };

    const firstDraft = transformCardToDraft(firstCard);
    const secondDraft = transformCardToDraft(secondCard);

    if (selectedCards.length === 0) {
        return (
            <Typography variant="normal" fontSize={'16px'} color="text.secondary">
                Select 2 physicians from the graph above to compare them
            </Typography>
        );
    }

    if (isFetching) {
        return <LoadingIndicator />;
    }
    
    return (
        <Stack direction={'row'}>
            <Stack p={2} width={'65%'}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="heavy">
                        Physician Card Comparison
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseRounded />
                    </IconButton>
                </Stack>
                
                {(firstCardId || secondCardId) && (
                    <Stack spacing={2}>            
                        <ReviewTabs 
                            selectedTab={selectedTab} 
                            setSelectedTab={setSelectedTab} 
                            variant="card_comparison"
                            physicianNames={{
                                first: firstCardId ? 'Dr. ' + firstDraft?.card.owner.last_name : 'Physician A',
                                second: secondCardId ? 'Dr. ' + secondDraft?.card.owner.last_name : 'Physician B'
                            }}
                            logUserAction={logUserAction}
                        />

                        <Stack sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                            {(selectedTab === 'physician_b' || selectedTab === 'comparison') && !secondCardId && (
                                <Typography variant="normal" fontSize={'14px'} color="text.secondary">
                                    Please select a second physician to compare.
                                </Typography>
                            )}
                            
                            {selectedTab === 'physician_a' && firstCardId && (
                                <CategoryView 
                                    physicianDraft={firstDraft}
                                    isLoading={false}
                                    variant="analytics_single"
                                    seeItemDetails={(item) => {
                                        setSelectedItem(selectedItem?.id === item.id ? null : item)
                                    }}
                                    defaultCategory={null}
                                    selectedItemId={selectedItem?.id}
                                />
                            )}
                            
                            {selectedTab === 'physician_b' && secondCardId && (
                                <CategoryView 
                                    peerDraft={secondDraft}
                                    isLoading={false}
                                    variant="peer_draft"
                                    seeItemDetails={(item) => {
                                        setSelectedItem(selectedItem?.id === item.id ? null : item)
                                    }}
                                    defaultCategory={null}
                                    selectedItemId={selectedItem?.id}
                                />
                            )}
                            
                            {selectedTab === 'comparison' && firstCardId && secondCardId && (
                                <CategoryView 
                                    physicianDraft={firstDraft}
                                    peerDraft={secondDraft}
                                    isLoading={false}
                                    variant="analytics_comparison"
                                    seeItemDetails={(item) => {
                                        setSelectedItem(selectedItem?.id === item.id ? null : item)
                                    }}
                                    defaultCategory={null}
                                    physicianNames={{
                                        first: firstCardId ? 'Dr. ' + (firstDraft?.card?.owner?.last_name) : 'Physician A',
                                        second: secondCardId ? 'Dr. ' + (secondDraft?.card?.owner?.last_name) : 'Physician B'
                                    }}
                                    selectedItemId={selectedItem?.id}
                                />
                            )}
                        </Stack>
                    </Stack>
                )}
            </Stack>
            <Stack 
                width={'35%'} 
                sx={{ 
                    position: 'sticky',
                    top: '60px',
                    alignSelf: 'flex-start',
                    maxHeight: '100vh - 60px',
                    overflow: 'auto'
                }}
            >
                <ItemDetails 
                    item={selectedItem}
                />
            </Stack>
        </Stack>
    );
}

export default CardComparison;